import {StyleSheet, css} from 'aphrodite';
import React, {useMemo} from 'react';

import {AnalysisGraphQL} from 'analysis-tool/AnalysisTypes';
import {getNullableStepFromAnalysis} from 'analysis-tool/data/getStepFromAnalysis';
import {checkIsAnalysisArchived, showFlowchartEditFunctionality} from 'analysis-tool/EditAnalysisHelpers';
import {CanEditReturnValue} from 'analysis-tool/EditAnalysisHelpers';
import {checkCanModifyStep} from 'analysis-tool/EditStepHelpers';
import {
  AnalysisFlowchartAction,
  AnalysisFlowchartAvailableAction,
} from 'analysis-tool/flowchart/actions/AnalysisFlowchartActionTypes';
import AnalysisFlowchart from 'analysis-tool/flowchart/AnalysisFlowchart';
import {AnalysisFlowchartMode} from 'analysis-tool/flowchart/AnalysisFlowchartContext';
import refreshAnalysis from 'analysis-tool/refreshAnalysis';
import {
  AnalysisRightSidebarItem,
  AnalysisRightSidebarItemType,
} from 'analysis-tool/right-sidebar/RightSidebarTypes';
import useRightSidebarItem from 'analysis-tool/ui/hooks/useRightSidebarItem';
import {AnalysisMainViewItemType, MainViewComponentProps} from 'analysis-tool/ui/main-view/MainViewTypes';

function getAvailableActions(
  analysis: AnalysisGraphQL,
  onChangeSelectedRightSidebarItem: AnalysisFlowchartViewProps['onChangeSelectedRightSidebarItem'],
  selectedRightSidebarItem: AnalysisRightSidebarItem | null
): Array<AnalysisFlowchartAvailableAction> {
  if (!showFlowchartEditFunctionality(analysis)) {
    return [];
  }

  const actions: Array<AnalysisFlowchartAvailableAction> = [
    {
      action: AnalysisFlowchartAction.OPEN_STEP_CONFIGURATION,
      onAction: ({stepId}: {stepId: string}) => {
        onChangeSelectedRightSidebarItem({type: AnalysisRightSidebarItemType.EDIT_STEP, stepId});
      },
    },
    {
      action: AnalysisFlowchartAction.ADD_SOURCE_STEP as AnalysisFlowchartAction.ADD_SOURCE_STEP,
      onAction: ({stepId}: {stepId: string}) => {
        onChangeSelectedRightSidebarItem({type: AnalysisRightSidebarItemType.EDIT_STEP, stepId});
      },
    },
    {
      action: AnalysisFlowchartAction.OPEN_ADD_STEP as AnalysisFlowchartAction.OPEN_ADD_STEP,
      onAction: ({stepId, stepDataName}: {stepId: string; stepDataName: string}) => {
        onChangeSelectedRightSidebarItem({
          type: AnalysisRightSidebarItemType.ADD_STEP,
          stepId,
          stepDataName,
        });
      },
    },
    {action: AnalysisFlowchartAction.CONNECT_STEPS},
    {action: AnalysisFlowchartAction.DUPLICATE_STEPS},
    {action: AnalysisFlowchartAction.DELETE_STEP},
    {action: AnalysisFlowchartAction.DELETE_STEP_AND_CHILDREN},
    {action: AnalysisFlowchartAction.RERUN_STEP},
  ];

  // Only include OPEN_ADD_SOURCE_STEP when sidebar is NOT open for source steps
  const isAddSourceStepSidebarOpen =
    selectedRightSidebarItem?.type === AnalysisRightSidebarItemType.ADD_SOURCE_STEP;

  if (!isAddSourceStepSidebarOpen) {
    actions.push({
      action: AnalysisFlowchartAction.OPEN_ADD_SOURCE_STEP,
      onAction: () => {
        onChangeSelectedRightSidebarItem({
          type: AnalysisRightSidebarItemType.ADD_SOURCE_STEP,
        });
      },
    });
  }

  return actions;
}

type AnalysisFlowchartViewProps = Pick<
  MainViewComponentProps<AnalysisMainViewItemType.FLOWCHART>,
  'analysis' | 'onChangeSelectedRightSidebarItem' | 'clientStateId'
>;

/**
 * A flowchart representation of an analysis, as rendered full-screen in the analysis tool.
 */
export default function AnalysisFlowchartView({
  analysis,
  onChangeSelectedRightSidebarItem,
  clientStateId,
}: AnalysisFlowchartViewProps): JSX.Element {
  const {selectedRightSidebarItem} = useRightSidebarItem(clientStateId);

  const canModifyStep = (stepId: string): CanEditReturnValue => {
    const step = getNullableStepFromAnalysis(analysis, stepId);
    if (step == null) {
      return {canEdit: false, message: 'This step has been deleted.'};
    }
    return checkCanModifyStep(analysis, stepId, 'step', 'modifyStep');
  };

  const availableActions = useMemo(
    () => getAvailableActions(analysis, onChangeSelectedRightSidebarItem, selectedRightSidebarItem),
    [analysis, onChangeSelectedRightSidebarItem, selectedRightSidebarItem]
  );

  return (
    <div className={css(styles.wrapper)}>
      <AnalysisFlowchart
        analysis={analysis}
        mode={AnalysisFlowchartMode.INTERACTIVE}
        availableActions={availableActions}
        canModifyStep={canModifyStep}
        canModifyAnalysis={() => checkIsAnalysisArchived(analysis) || {canEdit: true, message: null}}
        refresh={() => refreshAnalysis(analysis.id)}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    display: 'flex',
    flexDirection: 'row',
    height: '100%',
    width: '100%',
    position: 'relative',
  },
});
